<template>
  <ScrollArea class="h-[calc(100vh-16rem)]">
    <div class="space-y-4 pr-4">
    <!-- Header with Discover -->
    <div class="flex justify-between items-center">
      <p class="text-sm text-muted-foreground">
        Find WLED LED controllers on your network
      </p>
      <div class="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          @click="scanForWledAPs"
          :disabled="isScanningAPs"
          title="Find new WLED devices in AP mode"
        >
          <Wifi class="h-4 w-4 mr-2" :class="{ 'animate-pulse': isScanningAPs }" />
          {{ isScanningAPs ? 'Scanning...' : 'Find New' }}
        </Button>
        <Button
          variant="outline"
          size="sm"
          @click="discoverDevices"
          :disabled="isDiscovering"
        >
          <Search class="h-4 w-4 mr-2" :class="{ 'animate-spin': isDiscovering }" />
          {{ isDiscovering ? 'Scanning...' : 'Find on Network' }}
        </Button>
      </div>
    </div>

    <!-- Scan Status Message -->
    <div v-if="scanMessage && !scanError" class="p-3 bg-muted/50 rounded-lg">
      <p class="text-sm text-muted-foreground">{{ scanMessage }}</p>
    </div>

    <!-- Scan Error -->
    <div v-if="scanError" class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
      <p class="text-sm text-destructive">{{ scanError }}</p>
    </div>

    <!-- WLED Access Points (unconfigured devices) -->
    <div v-if="wledAccessPoints.length > 0" class="space-y-2">
      <h4 class="text-sm font-semibold flex items-center gap-2">
        <Wifi class="h-4 w-4 text-yellow-500" />
        Unconfigured WLED Devices
      </h4>
      <p class="text-xs text-muted-foreground mb-2">
        These WLED devices are broadcasting their own WiFi network. Add them to your network.
      </p>
      <div class="space-y-2">
        <div
          v-for="ap in wledAccessPoints"
          :key="ap.bssid"
          class="border border-yellow-500/30 bg-yellow-500/5 rounded-lg p-4 hover:bg-yellow-500/10 transition-colors"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <Wifi class="h-4 w-4 text-yellow-500 flex-shrink-0" />
                <h3 class="font-medium truncate">{{ ap.ssid }}</h3>
                <Badge variant="outline" class="text-yellow-600 border-yellow-500/50">
                  Setup Required
                </Badge>
              </div>
              <p class="text-xs text-muted-foreground">
                Signal: {{ ap.signalStrength }}dBm
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              @click="startProvisioning(ap)"
              class="border-yellow-500/50 text-yellow-600 hover:bg-yellow-500/10"
            >
              <Settings class="h-4 w-4 mr-1" />
              Setup
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Connected Devices -->
    <div v-if="connectedDevices.length > 0" class="space-y-4">
      <h4 class="text-sm font-semibold">Connected WLED Devices</h4>
      <div class="space-y-4">
        <div
          v-for="device in connectedDevices"
          :key="device.id"
          class="border rounded-lg overflow-hidden"
        >
          <!-- Device Header -->
          <div class="p-4 bg-muted/30 flex items-start justify-between">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <Cpu class="h-4 w-4 text-blue-500 flex-shrink-0" />
                <!-- Editable device name -->
                <template v-if="editingDeviceId === device.id">
                  <Input 
                    v-model="editingDeviceName" 
                    class="h-7 text-sm font-medium w-48"
                    @keyup.enter="saveDeviceName(device)"
                    @keyup.escape="cancelEditDevice"
                    @blur="saveDeviceName(device)"
                    ref="deviceNameInput"
                  />
                </template>
                <template v-else>
                  <h3 class="font-medium truncate">{{ device.name }}</h3>
                  <Button variant="ghost" size="sm" class="h-6 w-6 p-0" @click="startEditDevice(device)">
                    <Pencil class="h-3 w-3" />
                  </Button>
                </template>
                <Badge :variant="device.is_online ? 'default' : 'secondary'">
                  {{ device.is_online ? 'Online' : 'Offline' }}
                </Badge>
              </div>
              <p class="text-xs text-muted-foreground mb-1">{{ device.ip_address }}</p>
              <p class="text-xs text-muted-foreground">
                {{ device.led_count }} LEDs
                <span v-if="device.firmware_version" class="ml-2">v{{ device.firmware_version }}</span>
              </p>
            </div>
            <div class="flex items-center gap-2 ml-4">
              <Button
                variant="outline"
                size="sm"
                @click="syncDeviceOutputs(device)"
                :disabled="!device.is_online || syncingDeviceId === device.id"
                title="Sync outputs from device"
              >
                <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': syncingDeviceId === device.id }" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                @click="openWledInterface(device)"
                :disabled="!device.is_online"
                title="Open WLED web interface"
              >
                <ExternalLink class="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                @click="openDeleteDialog(device)"
                class="text-destructive hover:text-destructive"
                title="Remove device"
              >
                <Trash2 class="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <!-- Device Outputs/Segments -->
          <div class="p-4 space-y-3">
            <div class="flex items-center justify-between">
              <h5 class="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Segments</h5>
              <span class="text-xs text-muted-foreground">{{ getOutputsForDevice(device.id).length }} segment(s)</span>
            </div>
            
            <!-- No segments synced yet -->
            <div v-if="getOutputsForDevice(device.id).length === 0" class="text-center py-4 text-muted-foreground">
              <p class="text-sm">No segments synced yet</p>
              <Button 
                variant="outline" 
                size="sm" 
                class="mt-2"
                @click="syncDeviceOutputs(device)"
                :disabled="!device.is_online || syncingDeviceId === device.id"
              >
                <RefreshCw class="h-4 w-4 mr-2" :class="{ 'animate-spin': syncingDeviceId === device.id }" />
                Sync Segments
              </Button>
            </div>
            
            <!-- Output list -->
            <ScrollArea v-else class="h-48">
              <div class="space-y-2 pr-3">
                <div 
                  v-for="output in getOutputsForDevice(device.id)" 
                  :key="output.id"
                  class="border rounded-md p-3 bg-muted/20"
                >
                <div class="flex items-start justify-between">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <div 
                        class="w-3 h-3 rounded-full flex-shrink-0"
                        :class="output.is_rgbw ? 'bg-gradient-to-r from-red-500 via-green-500 to-white' : 'bg-gradient-to-r from-red-500 via-green-500 to-blue-500'"
                        :title="output.is_rgbw ? 'RGBW' : 'RGB'"
                      />
                      <!-- Editable output name -->
                      <template v-if="editingOutputId === output.id">
                        <Input 
                          v-model="editingOutputName" 
                          class="h-6 text-xs font-medium w-36"
                          @keyup.enter="saveOutputName(output)"
                          @keyup.escape="cancelEditOutput"
                          @blur="saveOutputName(output)"
                        />
                      </template>
                      <template v-else>
                        <span class="text-sm font-medium">
                          {{ output.name || `Segment ${output.output_index + 1}` }}
                        </span>
                        <Button variant="ghost" size="sm" class="h-5 w-5 p-0" @click="startEditOutput(output)">
                          <Pencil class="h-2.5 w-2.5" />
                        </Button>
                      </template>
                      <Badge variant="outline" class="text-xs">
                        {{ output.is_rgbw ? 'RGBW' : 'RGB' }}
                      </Badge>
                    </div>
                    <p class="text-xs text-muted-foreground">
                      LEDs {{ output.start_led }}-{{ output.start_led + output.led_count - 1 }} ({{ output.led_count }} LEDs)
                    </p>
                  </div>
                  <div class="flex items-center gap-2">
                    <!-- Light status for this output -->
                    <template v-if="getLightForOutput(device.id, output.output_index)">
                      <Badge variant="secondary" class="text-xs">
                        Light added
                      </Badge>
                      <Switch 
                        :modelValue="getLightForOutput(device.id, output.output_index)?.used_in_setup ?? false"
                        @update:modelValue="toggleOutputLightInSetup(device.id, output.output_index)"
                        title="Toggle in setup"
                      />
                    </template>
                    <Button
                      v-else
                      variant="outline"
                      size="sm"
                      @click="addLightForOutput(device, output)"
                      :disabled="isAddingLight"
                    >
                      <Plus class="h-3 w-3 mr-1" />
                      Add as Light
                    </Button>
                  </div>
                </div>
              </div>
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>

    <!-- Discovered Devices -->
    <div v-if="discoveredDevices.length > 0" class="space-y-2">
      <h4 class="text-sm font-semibold">Discovered Devices</h4>
      <div class="space-y-2">
        <div
          v-for="device in discoveredDevices"
          :key="device.macAddress"
          class="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
        >
          <div class="flex items-start justify-between">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <Cpu class="h-4 w-4 text-blue-500/50 flex-shrink-0" />
                <h3 class="font-medium truncate">{{ device.name }}</h3>
              </div>
              <p class="text-xs text-muted-foreground">{{ device.ipAddress }}</p>
              <p class="text-xs text-muted-foreground">{{ device.ledCount }} LEDs</p>
            </div>
            <Button
              size="sm"
              @click="addDevice(device)"
              :disabled="isAdding"
            >
              {{ isAdding ? 'Adding...' : 'Add' }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- Manual Add -->
    <div class="border-t pt-4 mt-4">
      <h4 class="text-sm font-semibold mb-2">Add by IP Address</h4>
      <div class="flex gap-2">
        <Input
          v-model="manualIp"
          placeholder="192.168.1.100"
          class="flex-1"
          @keyup.enter="addByIp"
        />
        <Button
          @click="addByIp"
          :disabled="!manualIp || isAdding"
          size="sm"
        >
          <Plus class="h-4 w-4 mr-1" />
          Add
        </Button>
      </div>
    </div>

    <!-- No Devices -->
    <div
      v-if="connectedDevices.length === 0 && discoveredDevices.length === 0 && wledAccessPoints.length === 0 && !isDiscovering && !isScanningAPs"
      class="text-center py-8 text-muted-foreground"
    >
      <Cpu class="h-12 w-12 mx-auto mb-3 opacity-50" />
      <p class="font-medium mb-1">No WLED devices found</p>
      <p class="text-sm mb-3">Make sure your WLED devices are on the same network or in AP mode</p>
      <div class="flex justify-center gap-2">
        <Button variant="outline" size="sm" @click="scanForWledAPs">
          <Wifi class="h-4 w-4 mr-2" />
          Find New Devices
        </Button>
        <Button variant="outline" size="sm" @click="discoverDevices">
          <Search class="h-4 w-4 mr-2" />
          Scan Network
        </Button>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
      <p class="text-sm text-destructive">{{ error }}</p>
    </div>

    <!-- Setup Dialog -->
    <Dialog v-model:open="showSetupDialog">
      <DialogContent class="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle class="flex items-center gap-2">
            <Wifi class="h-5 w-5 text-yellow-500" />
            Setup {{ provisioningAP?.ssid }}
          </DialogTitle>
          <DialogDescription>
            {{ setupStep === 'credentials' ? 'Enter your WiFi credentials to configure the WLED device' : 'Connect to the WLED network to complete setup' }}
          </DialogDescription>
        </DialogHeader>

        <!-- Step 1: Enter WiFi Credentials -->
        <div v-if="setupStep === 'credentials'" class="space-y-4 py-4">
          <div class="space-y-3">
            <div>
              <label class="text-sm font-medium">WiFi Network Name (SSID)</label>
              <Input v-model="wifiSsid" placeholder="Your home WiFi name" class="mt-1" />
            </div>
            <div>
              <label class="text-sm font-medium">WiFi Password</label>
              <Input v-model="wifiPassword" type="password" placeholder="Your WiFi password" class="mt-1" />
            </div>
          </div>
          
          <div class="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            <p class="font-medium mb-1">How this works:</p>
            <p>1. Connect your computer to "{{ provisioningAP?.ssid }}"</p>
            <p>2. Click "Configure Device" below</p>
            <p>3. The WLED will join your WiFi and become available</p>
          </div>
        </div>

        <!-- Step 2: Connect to WLED AP -->
        <div v-if="setupStep === 'connect'" class="space-y-4 py-4">
          <div class="p-4 border-2 border-dashed border-yellow-500/50 rounded-lg text-center">
            <Wifi class="h-8 w-8 mx-auto mb-2 text-yellow-500" />
            <p class="font-medium">Connect to WLED Network</p>
            <p class="text-sm text-muted-foreground mt-1">
              Open your WiFi settings and connect to:<br/>
              <span class="font-mono font-bold">{{ provisioningAP?.ssid }}</span>
            </p>
          </div>
          
          <div v-if="isConfiguring" class="flex items-center justify-center gap-2 py-2">
            <div class="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
            <span class="text-sm">{{ configureStatus }}</span>
          </div>
          
          <div v-if="configureError" class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p class="text-sm text-destructive">{{ configureError }}</p>
          </div>
        </div>

        <!-- Step 3: Success / Discover -->
        <div v-if="setupStep === 'success'" class="space-y-4 py-4">
          <div class="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
            <div class="h-8 w-8 mx-auto mb-2 rounded-full bg-green-500 flex items-center justify-center">
              <svg class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p class="font-medium text-green-600">WiFi Configured!</p>
            <p class="text-sm text-muted-foreground mt-1">
              The WLED device is now connecting to your network
            </p>
          </div>
          
          <p class="text-sm text-muted-foreground text-center">
            Reconnect your computer to your home WiFi, then click "Find on Network" to discover the device.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" @click="closeSetupDialog">Cancel</Button>
          
          <Button v-if="setupStep === 'credentials'" @click="proceedToConnect" :disabled="!wifiSsid || !wifiPassword">
            Next
          </Button>
          
          <Button v-if="setupStep === 'connect'" @click="configureWledDevice" :disabled="isConfiguring">
            <Wifi class="h-4 w-4 mr-2" />
            Configure Device
          </Button>
          
          <Button v-if="setupStep === 'success'" @click="finishSetupAndDiscover">
            <Search class="h-4 w-4 mr-2" />
            Find on Network
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <Dialog :open="deleteDialog.isOpen.value" @update:open="(v) => !v && deleteDialog.close()">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete WLED Device</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove "{{ deviceToDelete?.name }}"?
          </DialogDescription>
        </DialogHeader>
        
        <div class="py-4">
          <p class="text-sm text-muted-foreground">
            This will permanently delete the device and remove all associated lights and outputs.
            This action cannot be undone.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" @click="deleteDialog.close()">
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            @click="confirmDeleteDevice"
            :disabled="isDeleting"
          >
            {{ isDeleting ? 'Deleting...' : 'Delete' }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </div>
  </ScrollArea>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Cpu, Search, Trash2, Plus, Wifi, Settings, ExternalLink, Pencil, RefreshCw } from 'lucide-vue-next'
import { useWledDevicesStore, type WledDevice } from '@/stores/wledDevices'
import { useWledOutputsStore, type WledOutput } from '@/stores/wledOutputs'
import { useLightsStore, type Light } from '@/stores/lights'
import type { DiscoveredWledDevice, DetectedWifiNetwork } from '@/services/wled/WledDiscoveryService'
import { wledDiscoveryService } from '@/services/wled/WledDiscoveryService'
import { wledDeviceService } from '@/services/wled/WledDeviceService'
import { storeToRefs } from 'pinia'
import { Platform } from '@/lib/platform'
import { useDialog } from '@/composables/useDialog'

const wledStore = useWledDevicesStore()
const wledOutputsStore = useWledOutputsStore()
const lightsStore = useLightsStore()

const { allDevices, availableDiscovered, isDiscovering, isConnecting, error } = storeToRefs(wledStore)
const { allOutputs } = storeToRefs(wledOutputsStore)
const { allLights } = storeToRefs(lightsStore)

const manualIp = ref('')
const isAdding = ref(false)
const showSetupDialog = ref(false)
const isScanningAPs = ref(false)
const wledAccessPoints = ref<DetectedWifiNetwork[]>([])
const provisioningAP = ref<DetectedWifiNetwork | null>(null)
const scanError = ref<string | null>(null)
const scanMessage = ref<string | null>(null)

// Setup dialog state
const setupStep = ref<'credentials' | 'connect' | 'success'>('credentials')
const wifiSsid = ref('')
const wifiPassword = ref('')
const isConfiguring = ref(false)
const configureStatus = ref('')
const configureError = ref<string | null>(null)

const connectedDevices = computed(() => allDevices.value)
const discoveredDevices = computed(() => availableDiscovered.value)

// Device editing state
const editingDeviceId = ref<string | null>(null)
const editingDeviceName = ref('')
const syncingDeviceId = ref<string | null>(null)

// Output editing state
const editingOutputId = ref<string | null>(null)
const editingOutputName = ref('')
const isAddingLight = ref(false)

// Delete confirmation dialog
const deleteDialog = useDialog()
const deviceToDelete = ref<WledDevice | null>(null)
const isDeleting = ref(false)

// Get outputs for a device - use allOutputs for reactivity
const getOutputsForDevice = (deviceId: string) => {
  return allOutputs.value
    .filter((o: WledOutput) => o.wled_device_id === deviceId)
    .sort((a: WledOutput, b: WledOutput) => a.output_index - b.output_index)
}

// Get light for a specific output/segment - use allLights for reactivity
const getLightForOutput = (deviceId: string, segmentId: number) => {
  return allLights.value.find(
    (light: Light) => light.wled_device_id === deviceId && light.wled_segment_id === segmentId
  )
}

// Device name editing
const startEditDevice = (device: WledDevice) => {
  editingDeviceId.value = device.id
  editingDeviceName.value = device.name
  nextTick(() => {
    // Focus the input
    const input = document.querySelector('input[ref="deviceNameInput"]') as HTMLInputElement
    input?.focus()
  })
}

const cancelEditDevice = () => {
  editingDeviceId.value = null
  editingDeviceName.value = ''
}

const saveDeviceName = async (device: WledDevice) => {
  if (!editingDeviceName.value || editingDeviceName.value === device.name) {
    cancelEditDevice()
    return
  }
  
  // Update on WLED device
  if (device.is_online) {
    await wledDeviceService.setDeviceName(device.ip_address, editingDeviceName.value)
  }
  
  // Update in database
  await wledStore.updateDeviceName(device.id, editingDeviceName.value)
  cancelEditDevice()
}

// Segment name editing
const startEditOutput = (output: WledOutput) => {
  editingOutputId.value = output.id
  editingOutputName.value = output.name || `Segment ${output.output_index + 1}`
}

const cancelEditOutput = () => {
  editingOutputId.value = null
  editingOutputName.value = ''
}

const saveOutputName = async (output: WledOutput) => {
  if (!editingOutputName.value) {
    cancelEditOutput()
    return
  }
  
  await wledOutputsStore.updateOutputName(output.id, editingOutputName.value)
  cancelEditOutput()
}

// Sync outputs from device
const syncDeviceOutputs = async (device: WledDevice) => {
  syncingDeviceId.value = device.id
  try {
    await wledOutputsStore.syncOutputsFromDevice(device.id)
  } finally {
    syncingDeviceId.value = null
  }
}

// Add light for a specific output
const addLightForOutput = async (device: WledDevice, output: WledOutput) => {
  isAddingLight.value = true
  try {
    await lightsStore.registerWledSegmentLight({
      wledDeviceId: device.id,
      wledOutputId: output.id,
      segmentId: output.output_index,  // Use output index as segment ID
      name: output.name || `${device.name} - Segment ${output.output_index + 1}`,
      startLed: output.start_led,
      ledCount: output.led_count,
      isRgbw: output.is_rgbw
    })
  } finally {
    isAddingLight.value = false
  }
}

// Toggle light in setup for an output
const toggleOutputLightInSetup = async (deviceId: string, segmentId: number) => {
  const light = getLightForOutput(deviceId, segmentId)
  if (light) {
    await lightsStore.toggleLightInSetup(light.id)
  }
}

// Helper to get the light associated with a WLED device (legacy - for devices without segments)
const getLightForDevice = (deviceId: string) => {
  const lights = lightsStore.getLightsByWledDevice(deviceId)
  return lights[0] ?? null
}

const discoverDevices = async () => {
  if (!Platform.isTauri()) {
    wledStore.setError('WLED discovery not available in web browser')
    return
  }
  await wledStore.discoverDevices()
}

const scanForWledAPs = async () => {
  if (!Platform.hasCapability('wifiScan')) {
    wledStore.setError('WiFi scanning only available on desktop (macOS/Windows/Linux)')
    return
  }
  
  isScanningAPs.value = true
  scanError.value = null
  scanMessage.value = 'Scanning for WiFi networks...'
  
  try {
    const aps = await wledDiscoveryService.detectWledAccessPoints()
    wledAccessPoints.value = aps
    
    if (aps.length === 0) {
      scanMessage.value = 'No WLED-AP networks found. Make sure your new WLED device is powered on.'
    } else {
      scanMessage.value = `Found ${aps.length} WLED device(s) in setup mode`
    }
  } catch (err: any) {
    console.error('Failed to scan for WLED APs:', err)
    scanError.value = err?.message || 'Failed to scan for WiFi networks'
    scanMessage.value = null
  } finally {
    isScanningAPs.value = false
  }
}

const startProvisioning = (ap: DetectedWifiNetwork) => {
  provisioningAP.value = ap
  setupStep.value = 'credentials'
  wifiSsid.value = ''
  wifiPassword.value = ''
  configureError.value = null
  showSetupDialog.value = true
}

const proceedToConnect = () => {
  setupStep.value = 'connect'
  configureError.value = null
}

const configureWledDevice = async () => {
  isConfiguring.value = true
  configureError.value = null
  configureStatus.value = 'Connecting to WLED device...'
  
  try {
    // WLED AP is always at 4.3.2.1
    const WLED_AP_IP = '4.3.2.1'
    
    configureStatus.value = 'Sending WiFi credentials...'
    
    const success = await wledDeviceService.configureWifi(WLED_AP_IP, {
      ssid: wifiSsid.value,
      password: wifiPassword.value
    })
    
    if (success) {
      configureStatus.value = 'Configuration sent! Device is rebooting...'
      setupStep.value = 'success'
    } else {
      configureError.value = 'Failed to configure WiFi. Make sure you are connected to the WLED network.'
    }
  } catch (err: any) {
    console.error('Failed to configure WLED device:', err)
    configureError.value = err?.message || 'Failed to connect to WLED device. Make sure you are connected to the WLED-AP network.'
  } finally {
    isConfiguring.value = false
  }
}

const closeSetupDialog = () => {
  showSetupDialog.value = false
  setupStep.value = 'credentials'
  wifiSsid.value = ''
  wifiPassword.value = ''
  configureError.value = null
}

const finishSetupAndDiscover = async () => {
  closeSetupDialog()
  // Clear the AP from the list since user should have configured it
  wledAccessPoints.value = wledAccessPoints.value.filter(
    ap => ap.bssid !== provisioningAP.value?.bssid
  )
  provisioningAP.value = null
  // Trigger network discovery
  await discoverDevices()
}

const addDevice = async (device: DiscoveredWledDevice) => {
  isAdding.value = true
  try {
    const registered = await wledStore.registerDevice(device)
    if (registered) {
      // Sync outputs - this will auto-create lights for each segment
      await wledOutputsStore.syncOutputsFromDevice(registered.id)
    }
  } finally {
    isAdding.value = false
  }
}

const addByIp = async () => {
  if (!manualIp.value) return
  
  isAdding.value = true
  try {
    const device = await wledStore.addDeviceByIp(manualIp.value)
    if (device) {
      // Sync outputs - this will auto-create lights for each segment
      await wledOutputsStore.syncOutputsFromDevice(device.id)
      manualIp.value = ''
    }
  } finally {
    isAdding.value = false
  }
}

const openWledInterface = async (device: WledDevice) => {
  const url = `http://${device.ip_address}`
  
  if (Platform.isDesktop()) {
    try {
      const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow')
      const uniqueLabel = `wled-${device.id}-${Date.now()}`
      
      const webview = new WebviewWindow(uniqueLabel, {
        url,
        title: `WLED: ${device.name}`,
        width: 800,
        height: 600,
        center: true,
        resizable: true,
        fullscreen: false
      })
      
      webview.once('tauri://error', (e) => {
        console.error('Failed to create WLED window:', e)
        // Fallback to window.open
        window.open(url, '_blank')
      })
    } catch (err) {
      console.error('Failed to open Tauri window:', err)
      // Fallback to window.open
      window.open(url, '_blank')
    }
  } else {
    // In web browser, open in new tab
    window.open(url, '_blank')
  }
}

// Open delete confirmation dialog
const openDeleteDialog = (device: WledDevice) => {
  console.log('[WledDeviceSection] Opening delete dialog for:', device.id, device.name)
  deviceToDelete.value = device
  deleteDialog.open()
}

// Actually perform the delete
const confirmDeleteDevice = async () => {
  if (!deviceToDelete.value) return
  
  const device = deviceToDelete.value
  console.log('[WledDeviceSection] Starting delete for device:', device.id, device.name)
  isDeleting.value = true
  
  try {
    // Remove any lights associated with this device
    const wledLights = lightsStore.getLightsByWledDevice(device.id)
    console.log('[WledDeviceSection] Found lights to remove:', wledLights.length)
    for (const light of wledLights) {
      console.log('[WledDeviceSection] Removing light:', light.id)
      await lightsStore.removeLight(light.id)
    }
    
    // Remove outputs from local state (DB cascade handles the actual deletion)
    console.log('[WledDeviceSection] Removing outputs for device')
    wledOutputsStore.removeOutputsForDevice(device.id)
    
    console.log('[WledDeviceSection] Calling wledStore.removeDevice')
    const success = await wledStore.removeDevice(device.id)
    console.log('[WledDeviceSection] removeDevice result:', success)
    console.log('[WledDeviceSection] Current devices count:', allDevices.value.length)
    
    // Close dialog
    deleteDialog.close()
    deviceToDelete.value = null
  } finally {
    isDeleting.value = false
  }
}

onMounted(async () => {
  await wledStore.fetchDevices()
  await wledOutputsStore.fetchOutputs()
  // Auto-scan for WLED APs on mount
  if (Platform.isDesktop()) {
    scanForWledAPs()
  }
})
</script>
